import type { TableMetadataState } from '../internal';
import {
  normalizeColumnFilter,
  normalizeFilterGroup,
  type NxTableColumnFilter,
} from '../filterModel';
import type { NxServerTableQuery } from './serverTableTypes';

/**
 * Whether a filter value can go on the wire: a string, a boolean or a finite
 * number. `undefined` — typically a value the consumer has not resolved yet,
 * such as the id of a parent record still loading — `null`, NaN and objects
 * cannot: stringified they reached the server as `undefined`, `null` or
 * `[object Object]` and came back as a raw database error (I-1036).
 */
export function isResolvedFilterValue(value: unknown): value is string | number | boolean {
  return (
    typeof value === 'string' ||
    typeof value === 'boolean' ||
    (typeof value === 'number' && Number.isFinite(value))
  );
}

/** Columns of a filter map that hold at least one unresolved value. */
export function unresolvedFilterColumns(
  filters: ReadonlyMap<unknown, readonly unknown[]>
): string[] {
  return [...filters.entries()]
    .filter(([, values]) => values.some((value) => !isResolvedFilterValue(value)))
    .map(([column]) => String(column));
}

/** Values already checked with `isResolvedFilterValue`. */
function wireValue(values: readonly unknown[]): string | string[] {
  return values.length === 1 ? String(values[0]) : values.map(String);
}

/**
 * Throws for a base filter with an unresolved value. Dropping the entry would
 * widen the query to every row the base filter was meant to exclude, sending it
 * would put `undefined` on the wire: a consumer checks `unresolvedFilterColumns`
 * and waits for the value before it loads.
 */
export function tableStateToServerQuery<T extends Record<string, unknown>>(
  state: TableMetadataState<T>
): NxServerTableQuery {
  const filters: Record<string, NxServerTableQuery['filters'][string]> = {};

  const unresolved = unresolvedFilterColumns(state.baseFilterValues);
  if (unresolved.length > 0) {
    throw new Error(
      `Base filter ${unresolved.join(', ')} holds an unresolved value; load once it is resolved.`
    );
  }
  for (const [column, values] of state.baseFilterValues.entries()) {
    if (values.length > 0) {
      filters[String(column)] = wireValue(values);
    }
  }

  // Quick filters are what the user typed or picked; an unresolved value among
  // them is skipped rather than sent as text.
  for (const [column, values] of state.filterValues.entries()) {
    const resolved = values.filter((value) => isResolvedFilterValue(value));
    if (resolved.length > 0) {
      filters[String(column)] = wireValue(resolved);
    }
  }

  const conditions: Record<string, NxTableColumnFilter> = {};
  for (const [column, filter] of state.columnFilters?.entries() ?? []) {
    const normalized = normalizeColumnFilter(filter);
    if (normalized) conditions[String(column)] = normalized;
  }

  const advanced = normalizeFilterGroup(state.advancedFilter);

  return {
    filters,
    ...(Object.keys(conditions).length > 0 ? { conditions } : {}),
    ...(advanced ? { advanced } : {}),
    sort: state.columns
      .filter((col) => col.sortOrder)
      .sort((a, b) => a.sortPrio! - b.sortPrio!)
      .map((col) => ({
        columnId: String(col.columnId),
        asc: col.sortOrder === 'asc',
      })),
    page: Math.floor(state.pagination.offset / state.pagination.limit) + 1,
    pageSize: state.pagination.limit,
  };
}
